import type {
  AssetRelationType,
  InterestAssetMatchScore,
  LeadType,
  Ownership,
  ReadinessValue,
} from '@aikoa/common'
import { createAddressTitle, type Address } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { useMemo, type ReactNode } from 'react'
import { View } from 'react-native'
import ReadinessBadge from '~/components/feedback/FeedbackMatchBadge'
import Icon from '~/components/Icon'
import { InterestMatchBadge } from '~/components/interests/InterestMatchBadge'
import { getLeadName } from '~/components/leads/utils'
import { Dot } from '~/components/ui/dot'
import { Text } from '~/components/ui/text'
import { ItemTypeBadge } from '~/components/ui/TypeBadge'
import designTokens from '~/lib/designTokens'
import { cn } from '~/lib/utils'

type HighlightItem = {
  key: string
  content: ReactNode
}

type FeedHighlightsProps = {
  items: HighlightItem[]
  className?: string
  itemClassName?: string
  moreCount?: number
  moreTextClassName?: string
}

export function FeedHighlights({
  items,
  className,
  itemClassName,
  moreCount,
  moreTextClassName,
}: FeedHighlightsProps) {
  if (items.length === 0) {
    return null
  }

  return (
    <View className="gap-2">
      <View className={cn('border-y border-y-border', className)}>
        {items.map((item, index) => (
          <View
            key={item.key}
            className={cn(
              'flex-row items-center justify-between gap-2 border-t-border px-3 py-2 odd:bg-tertiary',
              index > 0 && 'border-t',
              itemClassName
            )}>
            {item.content}
          </View>
        ))}
      </View>
      {!!moreCount && moreCount > 0 && (
        <FeedHighlightsMoreCount moreCount={moreCount} textClassName={moreTextClassName} />
      )}
    </View>
  )
}

export function FeedHighlightsMoreCount({
  moreCount,
  textClassName,
}: {
  moreCount: number
  textClassName?: string
}) {
  const { t } = useTranslations('feed')

  return (
    <View className="flex-row items-center">
      <Text className={cn('font-light text-xs text-secondary', textClassName)}>
        +{moreCount} {t(($) => $.highlights.openCount, { count: moreCount })}
      </Text>
    </View>
  )
}

export type FeedHighlightItem = {
  assetId: string
  assetName: string
  address: Address | null
  isNew?: boolean
  itemDate?: string
}

export type MatchingAssetHighlight = FeedHighlightItem & {
  matchScores: InterestAssetMatchScore | null
  isOwn?: boolean
  relationType?: AssetRelationType
  organisationName?: string | null
  realtorName?: string | null
}

export type MatchingLeadHighlight = {
  leadId: string
  leadName: string
  matchScores: InterestAssetMatchScore | null
  feedbackQualification: ReadinessValue | null
  isNew?: boolean
  itemDate?: string
  isOwn?: boolean
  isPurchased?: boolean
  isVerified?: boolean
  organisationName?: string | null
  realtorName?: string | null
}

type AssetHighlightItemData = {
  id: string
  name: string
  isNew?: boolean
  itemDate?: string
  icon?: 'target' | 'message'
  matchScores?: InterestAssetMatchScore | null
  qualification?: ReadinessValue | null
  itemKind: 'asset'
  relationType: AssetRelationType
  ownership: Ownership
  organisationName?: string | null
  realtorName?: string | null
}

type LeadHighlightItemData = {
  id: string
  name: string
  isNew?: boolean
  itemDate?: string
  icon?: 'target' | 'message'
  matchScores?: InterestAssetMatchScore | null
  qualification?: ReadinessValue | null
  itemKind: 'lead'
  leadType: LeadType
  ownership: Ownership
  isPurchased?: boolean
  isVerified?: boolean
  organisationName?: string | null
  realtorName?: string | null
}

export type FeedHighlightItemData = AssetHighlightItemData | LeadHighlightItemData

type FeedHighlightItemsProps = {
  items: FeedHighlightItemData[]
  className?: string
  itemClassName?: string
  maxItems?: number
  moreCount?: number
  moreTextClassName?: string
}

export function FeedHighlightItems({
  items,
  className,
  itemClassName,
  maxItems,
  moreCount,
  moreTextClassName,
}: FeedHighlightItemsProps) {
  const { t } = useTranslations('feed')
  const { t: tLeads } = useTranslations('leads')

  const displayItems = useMemo(() => {
    const sorted = [...items].sort((itemA, itemB) => {
      if (itemA.isNew !== itemB.isNew) {
        return itemA.isNew ? -1 : 1
      }

      if (itemA.itemDate && itemB.itemDate) {
        return new Date(itemB.itemDate).getTime() - new Date(itemA.itemDate).getTime()
      }

      return 0
    })

    return maxItems ? sorted.slice(0, maxItems) : sorted
  }, [items, maxItems])

  const highlightItems = useMemo(
    () =>
      displayItems.map((item) => {
        let displayName: string

        if (item.itemKind === 'lead') {
          const resolvedName = getLeadName(item.name)

          if (resolvedName) {
            displayName = resolvedName
          } else if (item.ownership === 'own' || item.ownership === 'purchased') {
            displayName = item.isVerified
              ? tLeads(($) => $.verification.verifiedContact)
              : tLeads(($) => $.verification.newContact)
          } else {
            displayName = tLeads(($) => $.verification.verifiedBuyer)
          }
        } else {
          displayName = item.name
        }

        return {
          key: item.id,
          content: (
            <>
              <View className="gap-2">
                <View className="relative flex-row items-center gap-2">
                  <Text numberOfLines={1} className="font-medium text-sm text-primary">
                    {displayName}
                  </Text>
                  {item.isNew && <Dot variant="primary" />}
                </View>
                {item.itemKind === 'lead' && (
                  <ItemTypeBadge
                    itemKind="lead"
                    ownership={item.ownership}
                    organisationName={item.organisationName}
                    realtorName={item.realtorName}
                    size="sm"
                    variant="inverted"
                    className="gap-4"
                  />
                )}
                {item.itemKind === 'asset' && (
                  <ItemTypeBadge
                    itemKind="asset"
                    type={item.relationType}
                    ownership={item.ownership}
                    organisationName={item.organisationName}
                    realtorName={item.realtorName}
                    size="sm"
                    variant="inverted"
                    className="gap-4"
                  />
                )}
              </View>

              <View className="items-end gap-1">
                {item.matchScores && (
                  <InterestMatchBadge
                    scores={item.matchScores}
                    variant="inverted"
                    badgeClassName="py-0"
                  />
                )}
                {!item.matchScores && (
                  <ReadinessBadge
                    readiness={item.qualification ?? null}
                    label={item.icon ? undefined : t(($) => $.highlights.feedbackLabel)}
                    variant="inverted"
                  />
                )}
                <View className="flex-row items-center gap-1.5">
                  {item.icon === 'target' && (
                    <Icon
                      name="target"
                      library="materialcommunityicons"
                      size={13}
                      color={designTokens.secondary}
                      className="-ml-0.5 opacity-90"
                    />
                  )}
                  {item.icon === 'message' && (
                    <Icon
                      name="message-arrow-right-outline"
                      size={13}
                      color={designTokens.secondary}
                    />
                  )}
                  <Text className="text-secondary/90! font-normal text-xxs">
                    {item.icon === 'target'
                      ? t(($) => $.highlights.matchLabel)
                      : t(($) => $.highlights.feedbackLabel)}
                  </Text>
                </View>
              </View>
            </>
          ),
        }
      }),
    [displayItems, t, tLeads]
  )

  return (
    <FeedHighlights
      items={highlightItems}
      className={className}
      itemClassName={itemClassName}
      moreCount={moreCount}
      moreTextClassName={moreTextClassName}
    />
  )
}

function getAssetOwnership(isOwn?: boolean): Ownership {
  return isOwn ? 'own' : 'organisation'
}

export function createHighlightItemsFromAssets(params: {
  matchingAssets?: MatchingAssetHighlight[]
  maxTypeItems?: number
}): FeedHighlightItemData[] {
  const { matchingAssets = [], maxTypeItems } = params
  const items: FeedHighlightItemData[] = []

  let matchCount = 0

  for (const match of matchingAssets) {
    if (maxTypeItems && matchCount >= maxTypeItems) {
      break
    }

    items.push({
      id: match.assetId + 'match',
      name: createAddressTitle(match.address, false) || match.assetName,
      isNew: match.isNew,
      itemDate: match.itemDate,
      icon: 'target',
      matchScores: match.matchScores,
      itemKind: 'asset',
      relationType: match.relationType ?? 'representation',
      ownership: getAssetOwnership(match.isOwn),
      organisationName: match.organisationName,
      realtorName: match.realtorName,
    })

    matchCount++
  }

  return items
}

export function createHighlightItemsFromLeads(params: {
  leads: MatchingLeadHighlight[]
  maxTypeItems?: number
}): FeedHighlightItemData[] {
  const { leads, maxTypeItems } = params
  const items: FeedHighlightItemData[] = []

  let matchCount = 0
  let feedbackCount = 0

  for (const lead of leads) {
    const isMatch = !!lead.matchScores
    const isFeedback = !lead.matchScores && !!lead.feedbackQualification

    if (isMatch) {
      if (maxTypeItems && matchCount >= maxTypeItems) {
        continue
      }

      matchCount++
    }

    if (isFeedback) {
      if (maxTypeItems && feedbackCount >= maxTypeItems) {
        continue
      }

      feedbackCount++
    }

    const hasOrganisation = !!lead.organisationName

    let ownership: Ownership
    if (lead.isOwn) {
      ownership = 'own'
    } else if (lead.isPurchased) {
      // eslint-disable-next-line i18next/no-literal-string
      ownership = 'purchased'
    } else if (hasOrganisation) {
      ownership = 'organisation'
    } else {
      // eslint-disable-next-line i18next/no-literal-string
      ownership = 'network'
    }

    // eslint-disable-next-line i18next/no-literal-string
    const itemKind = 'lead'
    // eslint-disable-next-line i18next/no-literal-string
    const leadType = 'buyer'
    items.push({
      id: lead.leadId,
      name: lead.leadName ?? '',
      isNew: lead.isNew,
      itemDate: lead.itemDate,
      icon: isMatch ? 'target' : isFeedback ? 'message' : undefined,
      matchScores: lead.matchScores,
      qualification: lead.feedbackQualification,
      itemKind,
      leadType,
      ownership,
      isVerified: lead.isVerified,
      organisationName: lead.organisationName,
      realtorName: lead.realtorName,
    })
  }

  return items
}
